import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchMenuItems, fetchMenuCategories, fetchOrderingStatus, createOrder } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import Shift4CardForm, { useShift4Card } from "@/components/Shift4CardForm";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Minus, Plus, ShoppingCart, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

const NJ_TAX = 0.06625;
const CARD_ID = "shift4-order-card";

function parsePrice(raw) {
    if (!raw) return 0;
    const m = raw.match(/\$?([\d.]+)/);
    return m ? parseFloat(m[1]) : 0;
}

function CartLine({ item, qty, onAdd, onRemove }) {
    return (
        <div className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0">
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{item.name}</div>
                <div className="text-xs text-white/50">${(parsePrice(item.price) * qty).toFixed(2)}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button onClick={onRemove} className="p-1 rounded border border-white/10 hover:bg-white/5"><Minus size={12} /></button>
                <span className="w-5 text-center text-sm">{qty}</span>
                <button onClick={onAdd} className="p-1 rounded border border-white/10 hover:bg-white/5"><Plus size={12} /></button>
            </div>
        </div>
    );
}

export default function OrderPage() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState("all");
    const [cart, setCart] = useState({});
    const [step, setStep] = useState("browse"); // browse | checkout | success
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", phone: "", pickup_time: "", notes: "" });
    const [orderingStatus, setOrderingStatus] = useState(null);
    const { ready: cardReady, configured, tokenize } = useShift4Card(CARD_ID);
    const checkoutRef = useRef(null);

    useEffect(() => {
        Promise.all([fetchMenuItems({ is_active: true }), fetchMenuCategories(), fetchOrderingStatus().catch(() => null)])
            .then(([menuItems, cats, status]) => {
                setItems(menuItems.filter((i) => i.is_available !== false));
                setCategories(cats);
                setOrderingStatus(status);
            })
            .catch(() => {});
    }, []);

    const filtered = useMemo(
        () => activeCategory === "all" ? items : items.filter((i) => i.category === activeCategory),
        [items, activeCategory]
    );

    const addToCart = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
    const removeFromCart = (id) => setCart((c) => {
        const next = { ...c };
        if ((next[id] || 0) <= 1) delete next[id];
        else next[id] -= 1;
        return next;
    });

    const cartItems = useMemo(
        () => Object.entries(cart).map(([id, qty]) => ({ item: items.find((i) => i.id === id), qty })).filter((x) => x.item),
        [cart, items]
    );
    const subtotal = cartItems.reduce((s, { item, qty }) => s + parsePrice(item.price) * qty, 0);
    const tax = subtotal * NJ_TAX;
    const total = subtotal + tax;
    const cartCount = cartItems.reduce((s, { qty }) => s + qty, 0);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const goToCheckout = () => {
        if (!cartCount) { toast.error("Add items to your order first."); return; }
        if (orderingStatus && !orderingStatus.open) {
            toast.error("Online ordering is currently closed.");
            return;
        }
        setStep("checkout");
        setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    };

    const placeOrder = async () => {
        if (!form.name || !form.email || !form.phone) {
            toast.error("Name, email, and phone are required.");
            return;
        }
        if (configured && !cardReady) {
            toast.error("Card form is still loading. Please wait a moment.");
            return;
        }
        setSubmitting(true);
        try {
            const shift4Token = configured ? await tokenize() : "PLACEHOLDER_NOT_CONFIGURED";
            await createOrder({
                customer_name: form.name,
                customer_email: form.email,
                customer_phone: form.phone,
                items: cartItems.map(({ item, qty }) => ({
                    item_id: item.id,
                    name: item.name,
                    price: parsePrice(item.price),
                    quantity: qty,
                })),
                subtotal: Math.round(subtotal * 100) / 100,
                tax: Math.round(tax * 100) / 100,
                total: Math.round(total * 100) / 100,
                pickup_time: form.pickup_time,
                notes: form.notes,
                shift4_token: shift4Token,
            });
            setStep("success");
            setCart({});
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || "Order failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Order failed.");
        } finally {
            setSubmitting(false);
        }
    };

    if (step === "success") {
        return (
            <div data-testid="order-page">
                <section className="section">
                    <div className="container-narrow max-w-xl mx-auto text-center">
                        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                            <CheckCircle2 size={56} className="text-orange-300 mx-auto mb-4" />
                            <div className="font-display text-3xl text-white mb-2">Order Received!</div>
                            <p className="text-white/70 mb-6">Thanks, {form.name.split(" ")[0]}. Mo's on it — you'll hear back shortly with pickup confirmation.</p>
                            <button
                                className="ember-btn px-6 py-3 rounded-md font-display tracking-[0.12em] uppercase text-sm"
                                onClick={() => { setStep("browse"); setForm({ name: "", email: "", phone: "", pickup_time: "", notes: "" }); }}
                            >
                                Place Another Order
                            </button>
                        </motion.div>
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div data-testid="order-page">
            <section className="section">
                <div className="container-narrow">
                    <SectionHeader
                        eyebrow="Pre-Order"
                        title="Build Your Order"
                        subtitle="Pick your plates, pay online, and Mo has it ready when you roll up."
                    />

                    {orderingStatus && !orderingStatus.open && (
                        <motion.div
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-400/20 bg-yellow-500/10 px-4 py-4"
                        >
                            <Clock size={20} className="text-yellow-300 shrink-0 mt-0.5" />
                            <div>
                                <div className="font-display text-yellow-200 tracking-[0.05em]">Online ordering is closed right now</div>
                                <div className="text-sm text-yellow-100/70 mt-0.5">
                                    {orderingStatus.opens_at
                                        ? `Opens at ${orderingStatus.opens_at}. You can still browse the menu below.`
                                        : "Check back soon or call us to place an order."}
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Menu */}
                        <div className="lg:col-span-2">
                            {/* Category filter */}
                            <div className="flex flex-wrap gap-2 mb-5">
                                <button
                                    onClick={() => setActiveCategory("all")}
                                    className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-[0.18em] font-display border transition-colors ${activeCategory === "all" ? "bg-orange-500/20 border-orange-400/40 text-orange-300" : "border-white/10 text-white/60 hover:text-white"}`}
                                >All</button>
                                {categories.map((c) => (
                                    <button
                                        key={c.slug}
                                        onClick={() => setActiveCategory(c.slug)}
                                        className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-[0.18em] font-display border transition-colors ${activeCategory === c.slug ? "bg-orange-500/20 border-orange-400/40 text-orange-300" : "border-white/10 text-white/60 hover:text-white"}`}
                                    >{c.name}</button>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <AnimatePresence>
                                    {filtered.map((item) => {
                                        const qty = cart[item.id] || 0;
                                        const price = parsePrice(item.price);
                                        return (
                                            <motion.div
                                                key={item.id}
                                                layout
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.25 }}
                                            >
                                                <Card className="surface-card border-0 h-full">
                                                    <CardContent className="p-4 flex flex-col h-full">
                                                        <div className="flex items-start justify-between gap-2 flex-1">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-display text-base text-white leading-snug">{item.name}</div>
                                                                {item.description && (
                                                                    <div className="text-xs text-white/55 mt-1 line-clamp-2 leading-relaxed">{item.description}</div>
                                                                )}
                                                            </div>
                                                            {item.is_special && (
                                                                <Badge variant="outline" className="border-orange-400/40 text-orange-300 bg-orange-400/10 text-[9px] shrink-0">Special</Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center justify-between mt-3">
                                                            <div className="text-orange-300 font-display text-lg">${price.toFixed(2)}</div>
                                                            {qty === 0 ? (
                                                                <button
                                                                    onClick={() => addToCart(item.id)}
                                                                    className="ember-btn px-3 py-1.5 rounded-md text-xs font-display tracking-[0.12em] uppercase flex items-center gap-1"
                                                                ><Plus size={12} /> Add</button>
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    <button onClick={() => removeFromCart(item.id)} className="p-1.5 rounded border border-white/15 hover:bg-white/5"><Minus size={13} /></button>
                                                                    <span className="text-white font-medium w-4 text-center">{qty}</span>
                                                                    <button onClick={() => addToCart(item.id)} className="p-1.5 rounded border border-white/15 hover:bg-white/5"><Plus size={13} /></button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>
                            </div>
                        </div>

                        {/* Cart sidebar */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-24">
                                <Card className="surface-card border-0">
                                    <CardContent className="p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <ShoppingCart size={16} className="text-orange-300" />
                                            <span className="font-display text-lg tracking-[0.05em]">Your Order</span>
                                            {cartCount > 0 && (
                                                <Badge variant="outline" className="ml-auto border-orange-400/40 text-orange-300 bg-orange-400/10">{cartCount}</Badge>
                                            )}
                                        </div>

                                        {cartItems.length === 0 ? (
                                            <p className="text-white/45 text-sm">Nothing added yet. Pick something from the menu.</p>
                                        ) : (
                                            <>
                                                <div className="space-y-0 mb-4">
                                                    {cartItems.map(({ item, qty }) => (
                                                        <CartLine
                                                            key={item.id}
                                                            item={item}
                                                            qty={qty}
                                                            onAdd={() => addToCart(item.id)}
                                                            onRemove={() => removeFromCart(item.id)}
                                                        />
                                                    ))}
                                                </div>
                                                <div className="border-t border-white/10 pt-3 space-y-1 text-sm">
                                                    <div className="flex justify-between text-white/60">
                                                        <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-white/60">
                                                        <span>Tax (6.625%)</span><span>${tax.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-white font-medium text-base pt-1">
                                                        <span>Total</span><span>${total.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    className="ember-btn w-full mt-4 py-2.5 rounded-md text-sm font-display tracking-[0.12em] uppercase"
                                                    onClick={goToCheckout}
                                                >
                                                    {step === "checkout" ? "Review Checkout ↓" : "Checkout →"}
                                                </button>
                                            </>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </div>

                    {/* Checkout form */}
                    <AnimatePresence>
                        {step === "checkout" && (
                            <motion.div
                                ref={checkoutRef}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="mt-8"
                            >
                                <Card className="surface-card border-0 max-w-2xl">
                                    <CardContent className="p-6 sm:p-8 space-y-5">
                                        <div className="font-display text-2xl tracking-[0.05em]">Your Info</div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="sm:col-span-2">
                                                <Label className="text-white/80">Name *</Label>
                                                <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="First and last name" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                            </div>
                                            <div>
                                                <Label className="text-white/80">Email *</Label>
                                                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@example.com" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                            </div>
                                            <div>
                                                <Label className="text-white/80">Phone *</Label>
                                                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                            </div>
                                            <div>
                                                <Label className="text-white/80">Pickup Time</Label>
                                                <Input type="time" value={form.pickup_time} onChange={(e) => update("pickup_time", e.target.value)} className="mt-1 bg-black/40 border-white/10" />
                                            </div>
                                            <div>
                                                <Label className="text-white/80">Notes</Label>
                                                <Input value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Allergies, special requests..." className="mt-1 bg-black/40 border-white/10 focus:border-orange-400/50 placeholder:text-white/40" />
                                            </div>
                                        </div>

                                        <div>
                                            <Label className="text-white/80 block mb-2">Payment</Label>
                                            <Shift4CardForm containerId={CARD_ID} />
                                        </div>

                                        <div className="flex items-center justify-between pt-2">
                                            <button className="text-white/60 text-sm hover:text-white" onClick={() => setStep("browse")}>← Back to menu</button>
                                            <button
                                                className="ember-btn px-6 py-2.5 rounded-md text-sm font-display tracking-[0.12em] uppercase flex items-center gap-2"
                                                onClick={placeOrder}
                                                disabled={submitting}
                                            >
                                                {submitting ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : `Place Order · $${total.toFixed(2)}`}
                                            </button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>
        </div>
    );
}
